import {BaseAPI} from '@linx-security/api-specs';

/**
 * The base client type, which can be either the API client or a dummy client (for endpoints that are not yet implemented / skipped)
 */
export type BaseClient = BaseAPI | DummyApi;
class DummyApi extends BaseAPI {
  public skip() {
    return null;
  }
}

/**
 * The available endpoints in the client, omitting the `with` methods
 */
export type AvailableEndpoints<TClient extends BaseAPI = BaseClient> = Exclude<keyof TClient, `with${string}`>;

/**
 * Utility type to extract the request type from an endpoint
 */
export type ExtractEndpointRequestType<
  TClient extends BaseAPI,
  TEndpoint extends AvailableEndpoints<TClient>,
> = TClient[TEndpoint] extends ((args: any) => any) | ((args: any, _options: any) => any)
  ? Parameters<TClient[TEndpoint]>[0]
  : never;

/**
 * Utility type to extract the response type from an endpoint
 */
export type ExtractEndpointResponseType<
  TClient extends BaseAPI,
  TEndpoint extends AvailableEndpoints<TClient>,
> = TClient[TEndpoint] extends ((args: any) => any) | ((args: any, _options: any) => any)
  ? Awaited<ReturnType<TClient[TEndpoint]>>
  : never;

export type EndpointParams<
  TEndpoint extends AvailableEndpoints<TClient>,
  TClient extends BaseAPI | DummyApi = BaseAPI,
> = TClient[TEndpoint] extends (args: any) => any ? Parameters<TClient[TEndpoint]>[0] : never;

type Entity =
  | 'access-policy'
  | 'access-profiles'
  | 'access_profile_governance_logs'
  | 'access-request'
  | 'accounts'
  | 'application'
  | 'campaigns'
  | 'entitlements'
  | 'groups'
  | 'managed-groups'
  | 'human_accounts'
  | 'human_groups'
  | 'human_roles'
  | 'humans'
  | 'issues'
  | 'my_campaigns'
  | 'non_human_groups'
  | 'non_human_identities'
  | 'non_human_identity'
  | 'non_human_permissions'
  | 'non_human_resources'
  | 'non_human_roles'
  | 'orphan-accounts'
  | 'policies'
  | 'built-in-sod-policies'
  | 'reports'
  | 'reports_totals'
  | 'resources'
  | 'reviews'
  | 'roles'
  | 'saved_queries'
  | 'skip'
  | 'tags'
  | 'users'
  | 'violations'
  | 'workflow_action_runs'
  | 'workflow_actions'
  | 'workflow_triggers'
  | 'workflows'
  | 'access_policies'
  | 'access_policy'
  | 'access_policy_totals'
  | 'access_request'
  | 'access_request_applications'
  | 'access_request_application_entitlements'
  | 'access_request_available_applications'
  | 'human-access-profiles'
  | 'access-approval-totals'
  | 'access-approvals'
  | 'access-approval'
  | 'access-approval-decision'
  | 'my-access-requests-totals'
  | 'my-access-requests'
  | 'all-access-requests'
  | 'application_issue_heatmap'
  | 'application_identity_breakdown'
  | 'recently-joined-organization-insights'
  | 'access-request-reassign'
  | 'access-request-revoke'
  | 'ai-assistant-tools-manifest'
  | 'resources-count'
  | 'access-requests-request-status-breakdown'
  | 'access-requests-application-breakdown'
  | 'access-requests-identity-type-breakdown'
  | 'linx_audit_logs'
  | 'application-provisioning-settings'
  | 'ai_agent';
// TODO: Map TEntity to the actual entity type
export type CacheKey<
  _TEntity extends object = any,
  TClient extends BaseAPI = BaseClient,
  TEndpoint extends AvailableEndpoints<TClient> = AvailableEndpoints<TClient>,
> = {
  // Ideally we will get rid of this and use the entity instead
  endpoint: TEndpoint;
  entity: Entity | null;
  // Should be used when fetching entity by id
  id?: string | null;
  query?: EndpointParams<TEndpoint, TClient> | Record<string, any>;
};

export const isValidCacheKey = (cacheKey: any): cacheKey is CacheKey => {
  try {
    return typeof cacheKey.endpoint === `string`;
  } catch {
    console.warn(`Failed deserializing`, cacheKey);
    return false;
  }
};

/**
 * @param cacheKey String which is a key in the client
 * @param client An instance of the client
 * @returns  true if the cacheKey is a valid key in the client
 */
export const isLegacyCacheKey = <TClient extends DummyApi | BaseAPI = BaseAPI>(
  cacheKey: any,
  client: TClient,
): cacheKey is AvailableEndpoints<TClient> => {
  return (
    typeof cacheKey === `string` &&
    (cacheKey === `skip` || ((client as any)?.[cacheKey] && typeof (client as any)?.[cacheKey] === `function`))
  );
};
